import { useAuth } from "@/contexts/auth-context";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useNavigate, Link } from "react-router-dom";
import { useState, useRef, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { customInstance } from "@/lib/axios";
import { OrgSwitcher } from "./org-switcher";
import { ChevronDown } from "lucide-react";

interface TopbarProps {
  onMenuClick?: () => void;
}

interface NotificationItem {
  id: number;
  application_id: number;
  application_internal_id: string;
  event_type: string;
  event_type_display: string;
  subject: string;
  message_snippet: string;
  sent_at: string;
  read_at: string | null;
}

interface NotificationsResponse {
  unread_count: number;
  results: NotificationItem[];
}

export function Topbar({ onMenuClick }: TopbarProps) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchValue, setSearchValue] = useState("");
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [moreMenuOpen, setMoreMenuOpen] = useState(false);
  const notificationsRef = useRef<HTMLDivElement>(null);
  const moreMenuRef = useRef<HTMLDivElement>(null);

  const { data: notificationsData } = useQuery({
    queryKey: ["notifications"],
    queryFn: async () => {
      const { data } = await customInstance.get<NotificationsResponse>("/api/notifications/");
      return data;
    },
    enabled: !!user,
    refetchInterval: 60000,
    refetchOnWindowFocus: true,
  });

  const markReadMutation = useMutation({
    mutationFn: async (id: number) => {
      await customInstance.post(`/api/notifications/${id}/mark-read/`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["notifications"] });
    },
  });

  const markAllReadMutation = useMutation({
    mutationFn: async () => {
      await customInstance.post("/api/notifications/mark-all-read/");
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["notifications"] });
    },
  });

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      const t = event.target as Node;
      if (notificationsRef.current && !notificationsRef.current.contains(t)) {
        setNotificationsOpen(false);
      }
      if (moreMenuRef.current && !moreMenuRef.current.contains(t)) {
        setMoreMenuOpen(false);
      }
    }
    if (notificationsOpen || moreMenuOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [notificationsOpen, moreMenuOpen]);

  const unreadCount = notificationsData?.unread_count ?? 0;
  const results = notificationsData?.results ?? [];

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const handleSearch = () => {
    if (searchValue.trim()) {
      // Clear all filters and navigate to apps with only search parameter
      // This ensures we're searching across all applications, not just a filtered stage
      navigate(`/apps?search=${encodeURIComponent(searchValue.trim())}`);
      setSearchValue(""); // Clear after search
    } else {
      // Clear everything and go to all applications
      navigate("/apps");
      setSearchValue("");
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      handleSearch();
    }
  };

  const roleLabels = {
    SUPERADMIN: "Super Admin",
    DD_ACCOUNT_MANAGER: "DD Account Manager",
    ORG_ADMIN: "Org Admin",
    TERRITORY_SALES_MANAGER: "Territory Sales Manager",
    SALES: "Sales",
  };

  const showReports =
    user &&
    user.role !== "SALES" &&
    (user.role === "ORG_ADMIN" ||
      user.role === "TERRITORY_SALES_MANAGER" ||
      user.role === "DD_ACCOUNT_MANAGER" ||
      user.role === "SUPERADMIN");
  const showStips = user && (user.role === "DD_ACCOUNT_MANAGER" || user.role === "SUPERADMIN");
  const showMarketplaceSourceDealers = user && user.role === "SUPERADMIN";
  const showCreateOrg = user && (user.role === "SUPERADMIN" || user.role === "DD_ACCOUNT_MANAGER");
  const showCreateDealer = user && user.role === "TERRITORY_SALES_MANAGER";
  const showOrgSwitcher =
    !!user &&
    (user.is_superuser ||
      user.role === "SUPERADMIN" ||
      user.role === "DD_ACCOUNT_MANAGER" ||
      user.role === "TERRITORY_SALES_MANAGER" ||
      (user.role === "ORG_ADMIN" && user.organization_type === "MANUFACTURER"));
  const showOrgSettings =
    !!user &&
    (user.role === "ORG_ADMIN" || user.role === "DD_ACCOUNT_MANAGER" || user.role === "SUPERADMIN");
  const showAmSettings = user?.role === "DD_ACCOUNT_MANAGER";

  return (
    <div className="bg-white border-b border-gray-200">
      {/* Top row: Logo and Navigation */}
      <div className="h-16 px-3 sm:px-4 lg:px-6 flex items-center justify-between">
        <div className="flex items-center gap-2 sm:gap-3">
          {/* Hamburger menu button for mobile/tablet */}
          {onMenuClick && (
            <Button
              variant="ghost"
              size="sm"
              onClick={onMenuClick}
              className="lg:hidden mr-1"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </Button>
          )}
          <Link 
            to="/apps" 
            className="flex items-center gap-2 sm:gap-3 hover:opacity-80 transition-opacity cursor-pointer"
          >
            <img 
              src="/logo.png" 
              alt="DealerDog" 
              className="h-9 sm:h-11 w-auto"
            />
            <h1 className="text-base sm:text-lg lg:text-xl font-semibold text-gray-900 hidden sm:block">
              DealerDog Finance
            </h1>
          </Link>
        </div>
        <div className="flex items-center gap-2 sm:gap-3 lg:gap-4">
          {user && (
            <>
              {/* Desktop navigation */}
              <div className="hidden lg:flex items-center gap-3 shrink-0">
                {showOrgSwitcher && (
                  <div className="mr-1 max-w-[14rem] min-w-0">
                    <OrgSwitcher />
                  </div>
                )}
                <div className="relative" ref={moreMenuRef}>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="gap-1 text-gray-700"
                    aria-expanded={moreMenuOpen}
                    aria-haspopup="menu"
                    onClick={() => setMoreMenuOpen((o) => !o)}
                  >
                    More
                    <ChevronDown className="h-4 w-4 opacity-70" aria-hidden />
                  </Button>
                  {moreMenuOpen && (
                    <div
                      className="absolute right-0 top-full z-50 mt-1 min-w-[13rem] rounded-md border border-gray-200 bg-white py-1 shadow-lg"
                      role="menu"
                    >
                      {showReports && (
                        <Link
                          to="/admin/reports"
                          role="menuitem"
                          className="block px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
                          onClick={() => setMoreMenuOpen(false)}
                        >
                          Reports
                        </Link>
                      )}
                      {showMarketplaceSourceDealers && (
                        <Link
                          to="/admin/marketplace-source-dealers"
                          role="menuitem"
                          className="block px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
                          onClick={() => setMoreMenuOpen(false)}
                        >
                          Marketplace source dealers
                        </Link>
                      )}
                      {showStips && (
                        <Link
                          to="/admin/stips"
                          role="menuitem"
                          className="block px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
                          onClick={() => setMoreMenuOpen(false)}
                        >
                          Stips
                        </Link>
                      )}
                      {showCreateOrg && (
                        <Link
                          to="/staff/create-org"
                          role="menuitem"
                          className="block px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
                          onClick={() => setMoreMenuOpen(false)}
                        >
                          Create new org
                        </Link>
                      )}
                      {showCreateDealer && (
                        <Link
                          to="/tsm/create-dealer"
                          role="menuitem"
                          className="block px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
                          onClick={() => setMoreMenuOpen(false)}
                        >
                          Create dealer
                        </Link>
                      )}
                      {showOrgSettings && (
                        <Link
                          to="/settings/org"
                          role="menuitem"
                          className="block px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
                          onClick={() => setMoreMenuOpen(false)}
                        >
                          Org Settings
                        </Link>
                      )}
                      {showAmSettings && (
                        <Link
                          to="/settings/am"
                          role="menuitem"
                          className="block px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
                          onClick={() => setMoreMenuOpen(false)}
                        >
                          AM Settings
                        </Link>
                      )}
                      <Link
                        to="/notification-preferences"
                        role="menuitem"
                        className="block px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
                        onClick={() => setMoreMenuOpen(false)}
                      >
                        Notification preferences
                      </Link>
                    </div>
                  )}
                </div>
              </div>
              <div className="relative shrink-0" ref={notificationsRef}>
                <Button
                  variant="ghost"
                  size="sm"
                  className="relative p-2"
                  onClick={() => setNotificationsOpen((o) => !o)}
                  aria-label="Notifications"
                >
                  <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                  </svg>
                  {unreadCount > 0 && (
                    <span className="absolute -top-0.5 -right-0.5 flex h-4 w-4 items-center justify-center rounded-full bg-red-500 text-[10px] font-medium text-white">
                      {unreadCount > 99 ? "99+" : unreadCount}
                    </span>
                  )}
                </Button>
                {notificationsOpen && (
                  <div className="absolute right-0 top-full z-50 mt-1 w-80 rounded-md border border-gray-200 bg-white shadow-lg max-h-[min(24rem,70vh)] overflow-hidden flex flex-col">
                    <div className="flex items-center justify-between border-b border-gray-200 px-3 py-2">
                      <span className="font-medium text-sm text-gray-900">Notifications</span>
                      {unreadCount > 0 && (
                        <button
                          type="button"
                          onClick={() => markAllReadMutation.mutate()}
                          disabled={markAllReadMutation.isPending}
                          className="text-xs text-primary-600 hover:text-primary-700 font-medium disabled:opacity-50"
                        >
                          {markAllReadMutation.isPending ? "Clearing…" : "Clear all"}
                        </button>
                      )}
                    </div>
                    <div className="overflow-y-auto flex-1">
                      {results.length === 0 ? (
                        <p className="px-3 py-4 text-sm text-gray-500">No notifications</p>
                      ) : (
                        results.map((n) => (
                          <Link
                            key={n.id}
                            to={`/apps/${n.application_id}`}
                            className="block border-b border-gray-100 px-3 py-2.5 text-left hover:bg-gray-50 transition-colors"
                            onClick={() => {
                              if (!n.read_at) markReadMutation.mutate(n.id);
                              setNotificationsOpen(false);
                            }}
                          >
                            <div className="flex items-start justify-between gap-2">
                              <span className="text-xs font-medium text-gray-900">{n.application_internal_id}</span>
                              {!n.read_at && (
                                <span className="shrink-0 h-2 w-2 rounded-full bg-primary" aria-hidden />
                              )}
                            </div>
                            <p className="text-xs text-gray-700 mt-0.5 line-clamp-2">{n.subject || n.event_type_display}</p>
                            <p className="text-xs text-gray-500 mt-1">
                              {new Date(n.sent_at).toLocaleString(undefined, { dateStyle: "short", timeStyle: "short" })}
                            </p>
                          </Link>
                        ))
                      )}
                    </div>
                  </div>
                )}
              </div>
              <div className="hidden sm:flex flex-col items-end gap-1 min-w-0 lg:flex-row lg:items-center lg:gap-2 lg:max-w-[min(18rem,28vw)] text-xs sm:text-sm text-gray-700">
                <div className="flex items-center gap-2 min-w-0 max-w-full">
                  <span className="font-medium truncate">
                    {user.first_name} {user.last_name}
                  </span>
                  <span className="shrink-0 px-2 py-0.5 bg-blue-100 text-blue-700 rounded text-xs font-medium whitespace-nowrap">
                    {user.is_superuser ? "Super Admin" : roleLabels[user.role]}
                  </span>
                </div>
                {user.organization_name && (
                  <span className="shrink-0 max-w-full truncate px-2 py-0.5 bg-gray-100 text-gray-700 rounded text-xs font-medium">
                    {user.organization_name}
                  </span>
                )}
                <div className="lg:hidden flex items-center gap-2 flex-nowrap">
                  {(user.role === "SUPERADMIN" || user.role === "DD_ACCOUNT_MANAGER") && (
                    <Link
                      to="/staff/create-org"
                      className="text-xs font-medium text-blue-600 hover:text-blue-800 whitespace-nowrap shrink-0"
                    >
                      Create org
                    </Link>
                  )}
                  {showAmSettings && (
                    <Link
                      to="/settings/am"
                      className="text-xs font-medium text-blue-600 hover:text-blue-800 whitespace-nowrap shrink-0"
                    >
                      AM Settings
                    </Link>
                  )}
                  {user.role === "TERRITORY_SALES_MANAGER" && (
                    <Link
                      to="/tsm/create-dealer"
                      className="text-xs font-medium text-blue-600 hover:text-blue-800 whitespace-nowrap shrink-0"
                    >
                      Create dealer
                    </Link>
                  )}
                  {showOrgSwitcher && <OrgSwitcher />}
                </div>
              </div>
              <Link to="/account/password" className="shrink-0">
                <Button variant="outline" size="sm" className="text-xs sm:text-sm">
                  Change password
                </Button>
              </Link>
              <Button variant="outline" onClick={handleLogout} size="sm" className="text-xs sm:text-sm shrink-0">
                Logout
              </Button>
            </>
          )}
        </div>
      </div>
      
      {/* Bottom row: Search bar (desktop only) */}
      <div className="hidden lg:block border-t border-gray-200 px-3 sm:px-4 lg:px-6 py-2">
        <div className="flex items-center gap-2 max-w-md">
          <div className="relative flex-1">
            <svg 
              className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" 
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <Input
              type="text"
              placeholder="Search..."
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              onKeyDown={handleKeyDown}
              className="pl-10 pr-3"
            />
          </div>
          <Button 
            onClick={handleSearch}
            size="sm"
            className="shrink-0"
          >
            Search
          </Button>
        </div>
      </div>
    </div>
  );
}







